"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import "./MobilityPage.css";
import Modal from "../layout/Modal";
import {
  ExternalLink,
  Search,
  Filter,
  User,
  ChevronDown,
  X,
} from "lucide-react";
import MobilityUserProfileHeader from "./MobilityUserProfileHeader";
import Pagination from "../shared/paginator/paginator.js";
import CustomTooltip from "../shared/CustomTooltip.js";
import { getInternalMobilityAccess } from "../shared/access.js";
import { Info } from "lucide-react";

import { getAllJobRolesApi } from "../../services/jobRolesService";
import NoDataFound from "../shared/NoDataFound.js";
import EmptyState from "../shared/EmptyState.js";
import FilterNoResults from "../shared/FilterNoResults.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackButton from "../shared/backButton";
import { useTranslation } from "react-i18next";
import {
  getMobilityOpportunitiesApi,
  createMobilityRecommendationApi,
} from "../../services/upskillingService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import CustomReadinessFilter from "../shared/CustomReadinessFilter";
import { getDepartmentsListApi } from "../../services/settingsService";
import { currentPositionsApi } from "../../services/peopleService";

// Dummy data for match recommendations
const DUMMY_MATCH_DETAILS = {
  "Senior Developer": {
    skillOverlap: "JavaScript, React, Node.js, Database Design",
    reason:
      "Strong technical foundation with 5+ years of experience in full-stack development",
  },
  "Product Manager": {
    skillOverlap: "Strategic Planning, Team Leadership, Analytics",
    reason:
      "Demonstrated ability to communicate complex ideas and mentor junior team members",
  },
  "Technical Lead": {
    skillOverlap: "Architecture Design, Code Review, Team Management",
    reason:
      "Excellent problem-solving skills and proven track record in system design",
  },
  "QA Engineer": {
    skillOverlap: "Testing Methodologies, Automation, Attention to Detail",
    reason:
      "Analytical mindset and strong attention to quality assurance practices",
  },
  "Solutions Architect": {
    skillOverlap:
      "System Design, Client Communication, Technical Documentation",
    reason:
      "Strong ability to translate business requirements into technical solutions",
  },
};

const inFlightMobilityRequests = new Map();

const areStringListsEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const getInFlightMobilityRequest = (payload) => {
  const requestKey = JSON.stringify(payload ?? {});
  const existingRequest = inFlightMobilityRequests.get(requestKey);

  if (existingRequest) return existingRequest;

  const request = getMobilityOpportunitiesApi(payload).finally(() => {
    if (inFlightMobilityRequests.get(requestKey) === request) {
      inFlightMobilityRequests.delete(requestKey);
    }
  });

  inFlightMobilityRequests.set(requestKey, request);
  return request;
};

const MobilityPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramUserIds = searchParams.getAll("userId");
  const countHome = Number(searchParams.get("count") || 0);
  const hasCountInUrl = searchParams.has("count");
  const mobilityAccess = getInternalMobilityAccess();
  const removeCountFromUrl = () => {
    if (!searchParams.has("count")) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("count");
    setSearchParams(nextParams, { replace: true });
  };
  // const [showUserProfileHeader, setShowUserProfileHeader] = useState(
  //   paramUserIds.length === 1,
  // );
  const showUserProfileHeader = paramUserIds.length === 1;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterCurrentDept, setFilterCurrentDept] = useState("All");
  const [filterSuggestedDept, setFilterSuggestedDept] = useState("All");
  const [filterCurrentRole, setFilterCurrentRole] = useState("All");
  const [filterSuggestedRole, setFilterSuggestedRole] = useState("All");

  // NEW: read readiness from URL
  const readinessFromUrl = searchParams.getAll("readiness");

  const normalizeList = (arr, fallbackAll = ["All"]) => {
    const cleaned = Array.from(
      new Set((arr || []).map((x) => (x || "").trim()).filter(Boolean)),
    );
    if (!cleaned.length) return fallbackAll;

    const hasReal = cleaned.some((x) => x !== "All");
    if (hasReal) return cleaned.filter((x) => x !== "All");

    return fallbackAll;
  };

  const getInitialMobilityStatus = () => {
    // 1) if readiness is provided in URL, use it
    if (readinessFromUrl.length)
      return normalizeList(readinessFromUrl, ["All"]);

    // 2) keep your old behavior for userId deep links (optional)
    if (paramUserIds.length)
      return ["Mobility-ready", "Mobility-opportunities"];

    // 3) default = All
    return ["All"];
  };

  const [filterMobilityStatus, setFilterMobilityStatus] = useState(
    getInitialMobilityStatus,
  );

  const getRecommendationsNavigationPath = () => {
    const readinessFilters = normalizeList(filterMobilityStatus, []);

    if (!readinessFilters.length) {
      return "/talent-development/recommendations";
    }

    const nextParams = new URLSearchParams();
    readinessFilters.forEach((readiness) =>
      nextParams.append("readiness", readiness),
    );

    return `/talent-development/recommendations?${nextParams.toString()}`;
  };

  // keep state in sync if only query params change
  useEffect(() => {
    const nextMobilityStatus = getInitialMobilityStatus();

    setFilterMobilityStatus((prev) =>
      areStringListsEqual(prev, nextMobilityStatus) ? prev : nextMobilityStatus,
    );
    setCurrentPage((prev) => (prev === 1 ? prev : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const [sortBy, setSortBy] = useState("Match Percentage");

  const [showFilters, setShowFilters] = useState(false);

  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState({
    person: null,
    suggestedOpportunity: null,
  });
  const [recommendationReason, setRecommendationReason] = useState("");
  const [recommendationComment, setRecommendationComment] = useState("");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    person: "",
  });
  const [recommendation, setRecommendation] = useState(null);

  // State for expandable match details
  const [expandedMatches, setExpandedMatches] = useState({});

  // const [activeMobilityTab, setActiveMobilityTab] = useState("current");

  // ****************************************************
  // PAGINATION LOGIC
  const [upskillDataLength, setUpskillDataLength] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // const [currentPageCurrent, setCurrentPageCurrent] = useState(1);
  // const [currentPagePast, setCurrentPagePast] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);

  // const currentPage =
  //   activeMobilityTab === "current" ? currentPageCurrent : currentPagePast;
  // const setCurrentPage =
  //   activeMobilityTab === "current"
  //     ? setCurrentPageCurrent
  //     : setCurrentPagePast;

  const [isLoading, setIsLoading] = useState(false);
  const [isMobilityLoading, setIsMobilityLoading] = useState(true);
  const [mobilityData, setMobilityData] = useState([]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.max(
    1,
    Math.ceil((upskillDataLength || 0) / itemsPerPage),
  );

  // ******************************

  const [allWasClicked, setAllWasClicked] = useState(false);

  // Smart search & filter toggles (same as Employees)
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterCurrentDept && filterCurrentDept !== "All") count++;
    if (filterSuggestedDept && filterSuggestedDept !== "All") count++;
    if (filterCurrentRole && filterCurrentRole !== "All") count++;
    if (filterSuggestedRole && filterSuggestedRole !== "All") count++;
    if (
      filterMobilityStatus &&
      filterMobilityStatus.length > 0 &&
      !filterMobilityStatus.includes("All")
    )
      count++;
    if (sortBy && sortBy !== "Match Percentage") count++;
    return count;
  };

  const clearAllFilters = () => {
    removeCountFromUrl();
    setSearchTerm("");
    setSearchInputValue("");
    setFilterCurrentDept("All");
    setFilterSuggestedDept("All");
    setFilterCurrentRole("All");
    setFilterSuggestedRole("All");
    setFilterMobilityStatus(["All"]);
    setSortBy("Match Percentage");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  useEffect(() => {
    handleFilterData();
  }, [
    currentPage,
    searchTerm,
    itemsPerPage,
    filterCurrentDept,
    filterSuggestedDept,
    filterMobilityStatus,
    filterCurrentRole,
    filterSuggestedRole,
    sortBy,
    // activeMobilityTab,
  ]);

  useEffect(() => {
    getDepartmentsDropDownApiHandler();
    handleRolesDataListApi();
    getCurrentRolePositions();
  }, []);
  const handleFilterData = async ({ dedupe = true } = {}) => {
    try {
      setIsMobilityLoading(true); // Start mobility loader
      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: searchTerm,
        currentDepartment: filterCurrentDept,
        suggestedDepartment: filterSuggestedDept,
        currentPosition: filterCurrentRole,
        suggestedPosition: filterSuggestedRole,
        type:
          Array.isArray(filterMobilityStatus) && filterMobilityStatus.length
            ? filterMobilityStatus
            : undefined,
        sortBy: sortBy,
        userId: paramUserIds.map((id) => id),
      };
      const response = dedupe
        ? await getInFlightMobilityRequest(obj)
        : await getMobilityOpportunitiesApi(obj);
      setMobilityData(response.data);
      setUpskillDataLength(response.totalCount || null);
      if (response.code == 200) {
        setIsMobilityLoading(false);
      }
    } catch (error) {
      console.error("Error fetching upskill data:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsMobilityLoading(false); // End loader
    }
  };

  const [departments, setDepartments] = useState([]);
  // GET DEPARTMENTS DROPDOWN DATA
  const getDepartmentsDropDownApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      // ADD "ALL" OPTION IN THE SETDEPARTMENTS DROPDOWN TO SHOW ALL DEPARTMENTS IN INTIAL LOAD
      setDepartments([{ name: t("common.all"), _id: "All" }, ...response.data]);
      console.log("Departments fetched:", departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [currentRoles, setCurrentRoles] = useState([]);

  const [currentRolesPositions, setCurrentRolesPositions] = useState([]);

  const getCurrentRolePositions = async () => {
    try {
      const response = await currentPositionsApi();

      //  ADD ALL OPTION IN THE SETCURRENTROLESPOSITIONS DROPDOWN TO SHOW ALL ROLES IN INTIAL LOAD THE API RESPONSE IS ARRAY OF STRINGS

      setCurrentRolesPositions([
        { roleName: t("common.all") },
        ...response.data.map((role) => ({ roleName: role })),
      ]);
    } catch (error) {
      console.error("Error fetching current positions:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  // API call to get all job roles
  const handleRolesDataListApi = async () => {
    try {
      const response = await getAllJobRolesApi();
      // console.log("Job Roles fetched:", response);
      // setCurrentRoles(response.data);
      setCurrentRoles([
        { roleName: t("common.all"), _id: "All" },
        ...response.data,
      ]);
      console.log("Job Roles fetched:", currentRoles);

      // handle response if needed
    } catch (error) {
      console.error("Error fetching job roles:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // const handlePageChange = (pageNumber) => {
  //   setCurrentPage(pageNumber);

  //   console.log("Fetching data for page:", currentPage);
  // };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // ****************************************************

  const sortOptions = [
    {
      value: "Match Percentage",
      label: t("mobilityPage.filters.matchPercentage"),
    },
    { value: "Name", label: t("common.name") },
    { value: "Current Role", label: t("mobilityPage.current") },
    { value: "Suggested Role", label: t("mobilityPage.suggested") },
  ];

  // const mobilityStatusOptions = [
  //   "All",
  //   "Mobility-ready",
  //   "Mobility-opportunities",
  // ];
  // const READINESS_OPTIONS = ["All", "Mobility-ready", "Mobility-opportunities"];
  // const READINESS_LABELS = ["All", "Mobility-ready", "Mobility-opportunities"];

  const MOBILITY_STATUS_VALUES = [
    "All",
    "Mobility-ready",
    "Mobility-opportunities",
  ];

  const getMobilityStatusLabel = (value) => {
    switch (value) {
      case "All":
        return t("mobilityPage.statusOptions.all");
      case "Mobility-ready":
        return t("mobilityPage.statusOptions.mobilityReady");
      case "Mobility-opportunities":
        return t("mobilityPage.statusOptions.mobilityOpportunities");
      default:
        return value;
    }
  };

  const mobilityStatusOptions = MOBILITY_STATUS_VALUES;
  const READINESS_OPTIONS = MOBILITY_STATUS_VALUES;
  const READINESS_LABELS = MOBILITY_STATUS_VALUES.map(getMobilityStatusLabel);

  const [openReadiness, setOpenReadiness] = useState(false);
  const readinessRef = useRef(null);
  // Readiness options

  useEffect(() => {
    const onClickOutside = (e) => {
      if (readinessRef.current && !readinessRef.current.contains(e.target)) {
        setOpenReadiness(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const isAllSelected =
    filterMobilityStatus.length === READINESS_OPTIONS.length;
  const readinessDisplay =
    filterMobilityStatus.length === 0 ? "All" : filterMobilityStatus.join(", ");

  // Replace your toggleOption(...) with this:
  const toggleOption = (opt) => {
    if (opt === "All") {
      // Clicking "All" selects both statuses, and we remember that it was explicit
      setFilterMobilityStatus(READINESS_OPTIONS);
      setAllWasClicked(true);
      return;
    }
    setFilterMobilityStatus((prev) => {
      const next = prev.includes(opt)
        ? prev.filter((o) => o !== opt)
        : [...prev, opt];
      // Any manual toggle cancels the explicit "All" selection
      setAllWasClicked(false);
      return next;
    });
  };

  // Function to select mobility tooltip

  const mobilityReadinessTooltip = (option) => {
    if (option === "Mobility-ready")
      return t("mobilityPage.tooltips.filterTooltip.mobilityReady");
    if (option === "Mobility-opportunities")
      return t("mobilityPage.tooltips.filterTooltip.mobilityOpportunities");
    return "";
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      switch (sortBy) {
        case "match":
          return b.matchPercentage - a.matchPercentage;
        case "name":
          return a.name.localeCompare(b.name);
        case "currentRole":
          return a.currentRole.localeCompare(b.currentRole);
        case "suggestedRole":
          return a.suggestedRole.localeCompare(b.suggestedRole);
        default:
          return 0;
      }
    });
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 90) return "match-excellent";
    if (percentage >= 80) return "match-good";
    return "match-average";
  };

  const handleRecommendMove = (person, suggestedOpportunity) => {
    console.log("person:", person);
    console.log("suggestedOpportunity:", suggestedOpportunity);

    setSelectedOpportunity({
      person,
      suggestedOpportunity,
    });

    setRecommendationReason("");
    setRecommendationComment("");
    setShowRecommendModal(true);
  };
  const submitRecommendation = async () => {
    try {
      const person = selectedOpportunity?.person;
      const opportunity = selectedOpportunity?.suggestedOpportunity;

      const obj = {
        mobilityOpportunityId: opportunity?.mobilityOpportunityId,
        userId: person?._id || person?.userId,
        reason: recommendationReason,
        comments: recommendationComment,
      };

      console.log("Submitting recommendation payload:", obj);

      const response = await createMobilityRecommendationApi(obj);
      setShowRecommendModal(false);

      if (response.code === 201) {
        handleFilterData({ dedupe: false });

        setShowModal({
          show: true,
          showButton: false,
          type: "success",
          title: t("mobilityPage.recommendationModal.successTitle"),
          message: t("mobilityPage.recommendationModal.successMessage", {
            name: person?.name,
          }),
        });

        setSelectedOpportunity({
          person: null,
          suggestedOpportunity: null,
        });
        setRecommendationReason("");
        setRecommendationComment("");
      }
    } catch (error) {
      console.error("Error submitting recommendation:", error);
      handleApiError(error, {
        showModal: true,
      });
    }
  };
  const handlePersonClick = (person) => {
    navigate(`/employees/${person._id}`);
  };

  // generateExplainabilityText

  const generateExplainabilityText = (explainability) => {
    if (!explainability || typeof explainability !== "string") return "";

    // normalize CRLF -> LF
    const str = explainability.trim().replace(/\r\n/g, "\n");

    // insert a newline before any "•" that's not already at the start of a line
    const withBulletBreaks = str.replace(/([^\n])\s*•/g, "$1\n•");

    // avoid accidental double newlines before bullets
    return withBulletBreaks.replace(/\n{2,}•/g, "\n•");
  };

  // "Mobility-ready" == match >= 80; "Mobility-opportunities" == match < 80
  /*const displayedMobility =
  filterMobilityStatus === "All"
    ? mobilityData
    : mobilityData.filter((p) => {
        const pct = Number(p?.totalMatchPercentage ?? 0);
        const isReady = pct >= 80;
        return filterMobilityStatus === "Mobility-ready" ? isReady : !isReady;
      });*/

  const displayedMobility = mobilityData;
  // const displayedMobility = mobilityData.filter((person) => {
  //   if (activeMobilityTab === "current") {
  //     return person.isRecommended === false;
  //   } else {
  //     return person.isRecommended === true;
  //   }
  // });

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterCurrentDept && filterCurrentDept !== "All") count++;
    if (filterSuggestedDept && filterSuggestedDept !== "All") count++;
    if (filterCurrentRole && filterCurrentRole !== "All") count++;
    if (filterSuggestedRole && filterSuggestedRole !== "All") count++;
    if (
      filterMobilityStatus &&
      filterMobilityStatus.length > 0 &&
      !filterMobilityStatus.includes("All")
    )
      count++;
    if (sortBy && sortBy !== "Match Percentage") count++;
    return count;
  };

  const sanitizeSearch = (val) =>
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");

  const handleSearchButtonClick = () => {
    const nextSearchTerm = sanitizeSearch(searchInputValue);

    if (nextSearchTerm === searchTerm) {
      setShowSearchBar(false);
      return;
    }

    removeCountFromUrl();
    setCurrentPage(1);
    setSearchTerm(nextSearchTerm);
    setSearchInputValue(nextSearchTerm);
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    const hasAppliedSearch = Boolean(searchTerm);

    setSearchInputValue("");

    if (!hasAppliedSearch) return;

    removeCountFromUrl();
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handleSelect = (selectedList, selectedItem) => {
    removeCountFromUrl();

    if (selectedItem === "All") {
      setFilterMobilityStatus(["All"]);
      return;
    }

    const keys = selectedList.filter((k) => k !== "All");
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
  };

  const handleRemove = (selectedList, removedItem) => {
    removeCountFromUrl();

    const keys = selectedList.filter((k) => k !== "All");
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
  };
  // Toggle expandable match details
  const toggleMatchExpanded = (matchKey) => {
    setExpandedMatches((prev) => ({
      ...prev,
      [matchKey]: !prev[matchKey],
    }));
  };

  const CardSkeleton = () => (
    <div className="mobility-person-card skeleton-card">
      <div className="person-card-header">
        <div className="person-card-main-info">
          <div
            className="skeleton skeleton-text h-20 mb-8"
            style={{ width: "55%" }}
          />
          <div
            className="skeleton skeleton-text h-16 mb-6"
            style={{ width: "40%" }}
          />
          <div
            className="skeleton skeleton-text h-14"
            style={{ width: "28%" }}
          />
        </div>

        <div className="person-card-badge">
          <div
            className="skeleton skeleton-chip"
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "14px",
            }}
          />
        </div>
      </div>

      <div className="matches-title">
        <div className="skeleton skeleton-text h-14" style={{ width: "32%" }} />
      </div>

      {[1, 2, 3].map((item) => (
        <div key={item} className="person-card-matches">
          <div className="matches-list">
            <div className="match-item">
              <div className="match-info">
                <div
                  className="skeleton skeleton-text h-16 mb-6"
                  style={{ width: "48%" }}
                />
                <div
                  className="skeleton skeleton-text h-14 mb-8"
                  style={{ width: "36%" }}
                />

                <div className="match-score-inline">
                  <div
                    className="skeleton skeleton-pill"
                    style={{
                      width: "64px",
                      height: "24px",
                      borderRadius: "12px",
                    }}
                  />
                </div>

                <div className="match-details-container">
                  <div
                    className="skeleton skeleton-text h-14"
                    style={{ width: "34%", marginTop: "8px" }}
                  />
                </div>
              </div>

              <div
                className="skeleton skeleton-btn"
                style={{
                  width: "110px",
                  height: "36px",
                  borderRadius: "8px",
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
        </div>
      ))}

      <div className="person-card-footer">
        <div
          className="skeleton skeleton-btn"
          style={{
            width: "100%",
            height: "44px",
            borderRadius: "10px",
          }}
        />
      </div>
    </div>
  );

  // if (showUserProfileHeader && paramUserIds.length === 1) {
  //   return (
  //     <div className="mobility-page">
  //       <BackButton />
  //       <MobilityUserProfileHeader userId={paramUserIds[0]} />
  //     </div>
  //   );
  // }

  if (showUserProfileHeader && paramUserIds.length === 1) {
    return (
      <div className="mobility-page">
        <BackButton />
        <MobilityUserProfileHeader userId={paramUserIds[0]} />
      </div>
    );
  }

  return (
    <div className="mobility-page">
      <main className="mobility-main">
        <div className="container">
          <div className="page-top-action-row mobility-top-action-row">
            <BackButton />

            <button
              className="btn quick-link-btn page-top-cta"
              onClick={() => navigate("/talent-development/recommendations")}
            >
              <ExternalLink size={14} />
              {t("mobilityPage.viewAllRecommendations")}
            </button>
          </div>

          <div className="page-header">
            <div className="header-icon">
              <ExternalLink size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("mobilityPage.pageTitle")}</h1>
              <span className="mobility-title-info-wrap">
                <button
                  type="button"
                  className="mobility-title-info-btn"
                  aria-label={t("mobilityPage.tooltips.readiness.title")}
                >
                  <Info size={18} />
                </button>

                <span className="mobility-title-info-popover" role="tooltip">
                  <strong>{t("mobilityPage.tooltips.readiness.title")}</strong>
                  <span>{t("mobilityPage.tooltips.readiness.message")}</span>
                </span>
              </span>
            </div>
            <p>{t("mobilityPage.pageDescription")}</p>
          </div>

          {/* Smart Search & Filter panel (same as Employees) */}
          <div className="smart-filter-panel-team">
            <div className="smart-filter-top-bar">
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("mobilityPage.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) =>
                      setSearchInputValue(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("mobilityPage.searchPlaceholder")}
                  </span>
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={handleSearchButtonClick}
                  >
                    <Search size={18} />
                  </button>
                  {searchInputValue && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              {/* Icons pinned to the right */}
              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${
                      showSearchBar ? "active" : ""
                    }`}
                    onClick={() => {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${
                    showFiltersPanel ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>
              <div className="smart-filter-left">
                <div className="smart-filter-info">
                  <div className="div-container">
                    {getAppliedFiltersCount() > 0 && (
                      <>
                        <span className="smart-filter-badge">
                          {getAppliedFiltersCount()}
                        </span>
                        <span> {t("common.filtersApplied")}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>
                        {t("mobilityPage.filters.currentDepartment")}
                      </label>
                      <select
                        value={filterCurrentDept}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/settings/organization-info");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterCurrentDept(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterCurrentDept !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noDepartmentsAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createDepartment")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectDepartment")}
                            </option>
                            {departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>
                        {t("mobilityPage.filters.suggestedDepartment")}
                      </label>
                      <select
                        value={filterSuggestedDept}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/settings/organization-info");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterSuggestedDept(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterSuggestedDept !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noDepartmentsAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createDepartment")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectDepartment")}
                            </option>
                            {departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.currentRole")}</label>
                      <select
                        value={filterCurrentRole}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("job-roles");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterCurrentRole(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterCurrentRole !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {currentRolesPositions.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noRolesAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createRole")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectRole")}
                            </option>
                            {currentRolesPositions.map((role) => (
                              <option key={role._id} value={role.roleName}>
                                {role.roleName}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.suggestedRole")}</label>
                      <select
                        value={filterSuggestedRole}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/job-roles");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterSuggestedRole(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterSuggestedRole !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {currentRoles.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noRolesAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createRole")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectRole")}
                            </option>
                            {currentRoles.map((role) => (
                              <option key={role._id} value={role.roleName}>
                                {role.roleName}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.sortBy")}</label>
                      <select
                        value={sortBy}
                        onChange={(e) => {
                          removeCountFromUrl();
                          setSortBy(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          sortBy !== "Match Percentage" ? "smart-active" : ""
                        }`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.readiness")}</label>
                      <CustomReadinessFilter
                        options={READINESS_OPTIONS}
                        selectedValues={filterMobilityStatus}
                        onSelect={handleSelect}
                        onRemove={handleRemove}
                        label={t("mobilityPage.filters.readiness")}
                        getTooltip={mobilityReadinessTooltip}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* show the count of promoted candidates */}
          {hasCountInUrl &&
            countHome > 0 &&
            !isMobilityLoading &&
            countHome - (upskillDataLength || 0) > 0 && (
              <div className="mobility-pipeline-banner" role="status">
                <div className="mobility-pipeline-banner__content">
                  <span className="mobility-pipeline-banner__eyebrow">
                    <span className="mobility-pipeline-banner__eyebrow">
                      {t("mobilityPage.pipelineBanner.eyebrow")}
                    </span>
                  </span>
                  <p className="mobility-pipeline-banner__title">
                    {t("mobilityPage.pipelineBanner.title")}
                  </p>
                  <p className="mobility-pipeline-banner__summary">
                    <strong>{countHome - (upskillDataLength || 0)}</strong> of{" "}
                    <strong>{countHome}</strong> mobility-ready candidates are
                    already recommended
                  </p>
                </div>
                <button
                  type="button"
                  className="mobility-pipeline-banner__action"
                  onClick={() => navigate(getRecommendationsNavigationPath())}
                >
                  {t("mobilityPage.pipelineBanner.viewRecommendations")}
                  <ExternalLink size={14} />
                </button>
              </div>
            )}

          {/* <div className="mobility-tabs-container">
            <div className="mobility-tabs-nav">
              <button
                className={`mobility-tab-btn ${
                  activeMobilityTab === "current" ? "mobility-tab-active" : ""
                }`}
                onClick={() => setActiveMobilityTab("current")}
              >
                Current Recommendations
              </button>
              <button
                className={`mobility-tab-btn ${
                  activeMobilityTab === "past" ? "mobility-tab-active" : ""
                }`}
                onClick={() => setActiveMobilityTab("past")}
              >
                Past Recommendations
              </button>
            </div>
          </div> */}

          {/* {isLoading ? ( */}
          {isMobilityLoading ? (
            <div className="mobility-persons-grid" aria-busy="true">
              {Array.from({ length: Math.min(itemsPerPage, 12) }).map(
                (_, i) => (
                  <CardSkeleton key={i} />
                ),
              )}
            </div>
          ) : displayedMobility.length === 0 ? (
            getAppliedFiltersCount() > 0 ? (
              hasCountInUrl ? null : (
                <FilterNoResults
                  title={t("common.noResults")}
                  message={t("common.adjustOrClear")}
                  clearLabel={t("common.clearFilters")}
                  onClear={clearAllFilters}
                />
              )
            ) : (
              <EmptyState
                icon={ExternalLink}
                iconSize={48}
                title={t("mobilityPage.emptyTitle")}
                message={t("mobilityPage.emptyMessage")}
                actionLabel={t("common.goToEmployees")}
                onAction={() => navigate("/employees")}
              />
            )
          ) : (
            /*
            <div className="mobility-grid">
              {displayedMobility.map((person) => (
                <div
                  key={person.id}
                  className="mobility-card"
                  onClick={() => handlePersonClick(person)}
                >
                  <div className="card-header">
                    <div className="person-info">
                      <h3>{person.name}</h3>
                    </div>
                    <div className="mobility-match-score-container">
                      <div
                        className={`match-score ${getMatchColor(
                          person.totalMatchPercentage,
                        )}`}
                      >
                        {person.totalMatchPercentage}% {t("mobilityPage.match")}
                        <Info size={14} className="mobility-score-info-icon" />
                      </div>
                      <div className="mobility-score-hover-box">
                        <p
                          dangerouslySetInnerHTML={{
                            __html: generateExplainabilityText(
                              person.explainability,
                            ).replace(/\n/g, "<br/>"),
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="role-transition">
                    <div className="current-role">
                      <h4>{t("mobilityPage.current")}</h4>
                      <p className="role-title">{person.currentPosition}</p>
                      <p className="dept-name">{person.currentDepartment}</p>
                    </div>

                    <div className="arrow">→</div>

                    <div className="suggested-role">
                      <h4>{t("mobilityPage.suggested")}</h4>
                      <p className="role-title">{person.suggestedPosition}</p>
                      <p className="dept-name">{person.suggestedDepartment}</p>
                    </div>
                  </div>

                  {/* {activeMobilityTab === "current" && (
                    <button
                    className="btn btn-primary recommend-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRecommendMove(person);
                    }}
                  >
                    {t("mobilityPage.recommendMove")}
                  </button>
                  )} */

            /* Show recommend button for all cards */
            /* <button
                    className="btn btn-primary recommend-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRecommendMove(person);
                    }}
                  >
                    {t("mobilityPage.recommendMove")}
                  </button>
                </div>
              ))}
            </div>
            */

            <div className="mobility-grid">
              {displayedMobility.map((person) => (
                <div
                  key={person._id || person.userId}
                  className="mobility-person-card"
                  onClick={() => handlePersonClick(person)}
                >
                  <div className="person-card-header">
                    <div className="person-card-main-info">
                      <h3 className="person-card-name">{person.name}</h3>
                      <p className="person-card-role">
                        {person.currentPosition}
                      </p>
                      <p className="person-card-department">
                        {person.currentDepartment}
                      </p>
                    </div>
                    <div className="person-card-badge">
                      <div className="person-card-matches-count">
                        <span className="matches-number">
                          {person.matchesCount}
                        </span>
                        <span className="matches-label">
                          {t("mobilityPage.cards.matches")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <h4 className="matches-title">
                    {t("mobilityPage.cards.topOpportunities")}
                  </h4>

                  {person.topOpportunities.map((opp, index) => {
                    const matchKey = `${person._id || person.userId}-${index}`;

                    return (
                      <div key={matchKey} className="person-card-matches">
                        <div className="matches-list">
                          <div className="match-item">
                            <div className="match-info">
                              <p className="match-position">
                                {opp.suggestedPosition}
                              </p>
                              <p className="match-department">
                                {opp.suggestedDepartment}
                              </p>

                              <div className="match-score-inline">
                                <span className="score-badge match-excellent">
                                  {opp.totalMatchPercentage}%
                                </span>
                              </div>

                              <div className="match-details-container">
                                <button
                                  className="match-details-toggle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMatchExpanded(matchKey);
                                  }}
                                >
                                  <ChevronDown
                                    size={16}
                                    style={{
                                      transform: expandedMatches[matchKey]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s ease",
                                    }}
                                  />
                                  {t("mobilityPage.cards.whyThisMatch")}
                                </button>

                                {expandedMatches[matchKey] && (
                                  <div className="match-details-content">
                                    <div className="detail-item">
                                      <p>{opp.explainability}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              disabled={!mobilityAccess.canEditInternalMobility}
                              className="match-recommend-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRecommendMove(
                                  person,
                                  person.topOpportunities[index],
                                );
                              }}
                              title="Recommend for this role"
                            >
                              {t("mobilityPage.cards.recommend")}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="person-card-footer">
                    <button
                      className="view-all-matches-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/talent-development/mobility?userId=${person._id || person.userId}&source=mobility&readiness=All`,
                        );
                      }}
                    >
                      {t("mobilityPage.cards.viewAllMatches", {
                        count: person?.matchesCount,
                      })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {upskillDataLength != null && (
            <Pagination
              // loading={isLoading} // ← NEW
              loading={isMobilityLoading}
              total={upskillDataLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={mobilityData.length}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((upskillDataLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("mobilityPage.paginationInfo", { start, end, total })
              }
            />
          )}
        </div>
      </main>
      <Modal
        showButton={showModal.showButton}
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            person: "",
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
      />

      {/* Recommendation Modal */}
      {showRecommendModal && selectedOpportunity && (
        <Modal
          show={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          type="info"
          title={t("mobilityPage.recommendationModal.title")}
          showButton={false}
        >
          <div className="mobility-recommendation-modal-content">
            <div className="mobility-recommendation-info">
              <div className="mobility-recommendation-employee">
                <div className="mobility-recommendation-avatar">
                  <User size={32} />
                </div>
                <div className="mobility-recommendation-details">
                  <h4>{selectedOpportunity.person?.name}</h4>
                  <p>
                    {selectedOpportunity.person?.currentPosition} →{" "}
                    {
                      selectedOpportunity.suggestedOpportunity
                        ?.suggestedPosition
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="mobility-reason-section">
              <label className="mobility-reason-label">
                {t("mobilityPage.recommendationModal.primaryReason")}
              </label>
              <div className="mobility-reason-options">
                <label className="mobility-reason-option">
                  <input
                    type="radio"
                    name="recommendationReason"
                    value="Soft Skills Development"
                    checked={recommendationReason === "Soft Skills Development"}
                    onChange={(e) => setRecommendationReason(e.target.value)}
                  />
                  <div className="mobility-reason-content">
                    <strong>
                      {t(
                        "mobilityPage.recommendationModal.softSkillsDevelopment",
                      )}
                    </strong>
                    <p>
                      {t(
                        "mobilityPage.recommendationModal.softSkillsDescription",
                      )}
                    </p>
                  </div>
                </label>
                <label className="mobility-reason-option">
                  <input
                    type="radio"
                    name="recommendationReason"
                    value="Technical/Job Skills"
                    checked={recommendationReason === "Technical/Job Skills"}
                    onChange={(e) => setRecommendationReason(e.target.value)}
                  />
                  <div className="mobility-reason-content">
                    <strong>
                      {t("mobilityPage.recommendationModal.technicalJobSkills")}
                    </strong>
                    <p>
                      {t(
                        "mobilityPage.recommendationModal.technicalJobSkillsDescription",
                      )}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="mobility-comment-section">
              <label
                htmlFor="recommendation-comment"
                className="mobility-comment-label"
              >
                {t("mobilityPage.recommendationModal.additionalComments")}
              </label>
              <textarea
                id="recommendation-comment"
                value={recommendationComment}
                onChange={(e) => setRecommendationComment(e.target.value)}
                placeholder={t(
                  "mobilityPage.recommendationModal.commentsPlaceholder",
                )}
                className="mobility-comment-textarea"
                rows={4}
              />
            </div>

            <div className="mobility-modal-actions">
              <button
                className="mobility-modal-btn mobility-secondary"
                onClick={() => setShowRecommendModal(false)}
              >
                {t("mobilityPage.recommendationModal.cancel")}
              </button>
              <button
                className="mobility-modal-btn mobility-primary"
                onClick={submitRecommendation}
                disabled={!recommendationReason}
              >
                {t("mobilityPage.recommendationModal.submitRecommendation")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default MobilityPage;
